// src/components/material/AppButton.js
// Botão canônico do ProGERAR — 4 variants, 1 altura padrão (2026-05-11, Fase 11).
//
// Variants:
//   primary     — contained orange, ação primária (Salvar, Criar, Confirmar)
//   secondary   — outlined orange, ação neutra destacada (Filtros, Voltar)
//   tertiary    — text/transparent, ação subordinada (Cancelar, Limpar)
//   destructive — contained red, ação grave (Excluir)
//
// Tamanhos:
//   default — 40px (uso em PageActions, DataToolbar, FormActions)
//   small   — 32px (APENAS dentro de tabelas/chips)
//
// Radius: 8 (token md, já no theme).
//
// Substitui gradualmente components/material/Button.js (legacy COLORS.GREY/WHITE/etc).

import React from 'react'
import { Button as MuiButton, CircularProgress } from '@mui/material'

const VARIANT_STYLES = {
  primary: {
    variant: 'contained',
    sx: {
      bgcolor: 'primary.main',
      color: 'common.white',
      '&:hover': { bgcolor: 'primary.dark' },
      '&.Mui-disabled': {
        bgcolor: 'action.disabledBackground',
        color: 'text.disabled',
      },
    },
  },
  secondary: {
    variant: 'outlined',
    sx: {
      borderColor: 'primary.main',
      color: 'primary.main',
      bgcolor: 'background.paper',
      '&:hover': {
        bgcolor: 'rgba(234, 93, 13, 0.06)',
        borderColor: 'primary.dark',
      },
    },
  },
  tertiary: {
    variant: 'text',
    sx: {
      color: 'text.secondary',
      '&:hover': { bgcolor: 'action.hover' },
    },
  },
  destructive: {
    variant: 'contained',
    sx: {
      bgcolor: 'error.main',
      color: 'common.white',
      '&:hover': { bgcolor: 'error.dark' },
    },
  },
}

const SIZE_SX = {
  default: {
    minHeight: 40,
    px: 2.5,
    py: 1,
    fontSize: '0.875rem',
  },
  small: {
    minHeight: 32,
    px: 1.5,
    py: 0.5,
    fontSize: '0.8125rem',
  },
}

export default function AppButton({
  variant = 'primary',
  size = 'default',
  startIcon,
  endIcon,
  loading = false,
  disabled,
  onClick,
  fullWidth,
  type = 'button',
  children,
  sx,
  ...rest
}) {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.primary
  const sizeSx = SIZE_SX[size] || SIZE_SX.default

  return (
    <MuiButton
      type={type}
      variant={v.variant}
      onClick={onClick}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      startIcon={
        loading ? <CircularProgress size={14} color='inherit' /> : startIcon
      }
      endIcon={endIcon}
      sx={{
        borderRadius: '8px',
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        '&:hover': { boxShadow: 'none' },
        ...sizeSx,
        ...v.sx,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </MuiButton>
  )
}
